import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Download, Package, CheckCircle, Clock, LogOut, RefreshCw, DollarSign, Settings, Trash2, FileText, Tag, HardDrive } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0 });
  const [userRole, setUserRole] = useState('admin');

  useEffect(() => {
    document.title = 'Produkcija | Fotoexpres';
    const token = localStorage.getItem('adminToken');
    const role = localStorage.getItem('adminRole') || 'admin';
    setUserRole(role);
    if (!token) {
      navigate('/logovanje');
      return;
    }
    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOrders(response.data.orders);
      setStats(response.data.stats);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/logovanje');
      }
      toast({
        title: "Greška",
        description: "Nije moguće učitati porudžbine",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLogs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      toast({
        title: "Generisanje logova...",
        description: "Molimo sačekajte dok se kreira izveštaj"
      });
      
      const downloadUrl = `${API}/admin/download-logs`;
      
      const response = await axios.get(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'fotoexpres_logs.txt';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast({
        title: "Logovi preuzeti!",
        description: "Izveštaj je uspešno preuzet"
      });
    } catch (error) {
      console.error('Error downloading logs:', error);
      toast({
        title: "Greška",
        description: "Nije moguće preuzeti logove",
        variant: "destructive"
      });
    }
  };

  
  const handleCleanupOrphans = async () => {
    if (!window.confirm('Da li ste sigurni da zelite da ocistite fajlove obrisanih porudzbina sa servera?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(API + '/admin/cleanup-orphaned-files', {}, {
        headers: { 'Authorization': 'Bearer ' + token },
        timeout: 120000
      });
      toast({ title: "Ciscenje zavrseno", description: response.data.message, duration: 10000 });
    } catch (error) {
      toast({ title: "Greska", description: "Ciscenje nije uspelo", variant: "destructive" });
    }
  };

const handleDownload = async (orderNumber, zipFilePath) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Show initial loading toast
      toast({
        title: "⏳ Priprema preuzimanja...",
        description: `Porudžbina #${orderNumber} se priprema`,
      });
      
      // Create download URL
      const downloadUrl = `${API}/admin/orders/${orderNumber}/download`;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `order-${orderNumber}.zip`;
      
      // Add authorization header via fetch and blob
      const response = await axios.get(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
      
      // Show success toast
      toast({
        title: "✅ Preuzimanje započeto!",
        description: `Porudžbina #${orderNumber} se preuzima. Proverite folder za preuzimanja.`,
        duration: 5000,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "❌ Greška pri preuzimanju",
        description: error.response?.data?.detail || "ZIP fajl nije dostupan. Molimo sačekajte da se obrada završi ili osvežite stranicu.",
        variant: "destructive",
        duration: 6000,
      });
    }
  };

  const handleDelete = async (orderNumber) => {
    if (!window.confirm(`Da li ste sigurni da želite da obrišete porudžbinu ${orderNumber}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/orders/${orderNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast({
        title: "Porudžbina obrisana",
        description: `Porudžbina ${orderNumber} je uspešno obrisana`
      });
      
      fetchOrders();
    } catch (error) {
      toast({
        title: "Greška",
        description: "Nije moguće obrisati porudžbinu",
        variant: "destructive"
      });
    }
  };

  const handleStatusUpdate = async (orderNumber, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/orders/${orderNumber}/status`, 
        { status: newStatus },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      toast({
        title: "Status ažuriran",
        description: `Porudžbina ${orderNumber} je označena kao ${newStatus}`
      });
      fetchOrders();
    } catch (error) {
      toast({
        title: "Greška",
        description: "Nije moguće ažurirati status",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/logovanje');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Učitavanje...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600 mt-2">Upravljanje porudžbinama fotografija</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {userRole === 'admin' && (
              <>
                <Link to="/logovanje/prices">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-orange-600 text-orange-600 hover:bg-orange-50"
                  >
                    <DollarSign size={18} />
                    Cene
                  </Button>
                </Link>
                <Link to="/logovanje/discounts">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-purple-600 text-purple-600 hover:bg-purple-50"
                  >
                    <DollarSign size={18} />
                    Popusti
                  </Button>
                </Link>
                <Link to="/logovanje/promotion">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-pink-600 text-pink-600 hover:bg-pink-50"
                  >
                    <DollarSign size={18} />
                    Promocija
                  </Button>
                </Link>
                <Link to="/logovanje/products">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                  >
                    <Package size={18} />
                    Proizvodi
                  </Button>
                </Link>
                <Link to="/logovanje/promo-banner">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-orange-600 text-orange-600 hover:bg-orange-50"
                  >
                    <FileText size={18} />
                    Reklamni Baner
                  </Button>
                </Link>
                <Link to="/logovanje/promo-codes">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-purple-600 text-purple-600 hover:bg-purple-50"
                  >
                    <Tag size={18} />
                    Promo Kodovi
                  </Button>
                </Link>
                <Link to="/logovanje/settings">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-green-600 text-green-600 hover:bg-green-50"
                  >
                    <Settings size={18} />
                    Podešavanja
                  </Button>
                </Link>
                <Link to="/logovanje/password">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    <Settings size={18} />
                    Lozinka
                  </Button>
                </Link>
              </>
            )}
            {userRole === 'admin' && (
              <Button 
                onClick={handleDownloadLogs}
                variant="outline" 
                className="gap-2 border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
              >
                <FileText size={18} />
                Preuzmi Logove
              </Button>
            )}
            {userRole === 'admin' && (
              <Button 
                onClick={handleCleanupOrphans}
                variant="outline" 
                className="gap-2 border-2 border-red-600 text-red-600 hover:bg-red-50"
              >
                <HardDrive size={18} />
                Ocisti Disk
              </Button>
            )}
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="gap-2 border-2"
            >
              <LogOut size={18} />
              Odjavi se
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 mb-1">Ukupno Porudžbina</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Package className="w-12 h-12 text-orange-600" />
            </div>
          </Card>
          <Card className="p-6 border-2 border-yellow-200 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 mb-1">Na Čekanju</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600" />
            </div>
          </Card>
          <Card className="p-6 border-2 border-green-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 mb-1">Završeno</p>
                <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </Card>
        </div>

        {/* Orders Table */}
        <Card className="p-6 border-2 border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Porudžbine</h2>
            <Button onClick={fetchOrders} variant="outline" className="gap-2">
              <RefreshCw size={18} />
              Osveži
            </Button>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Nema porudžbina</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Broj Porudžbine</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Datum i Vreme</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Kupac</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Kontakt</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Fotografije / Proizvodi</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-4 px-4 font-mono font-semibold text-blue-600">{order.orderNumber}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {new Date(order.createdAt).toLocaleString('sr-RS', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-semibold">{order.contactInfo.fullName}</div>
                          <div className="text-sm text-gray-500">{order.contactInfo.street || order.contactInfo.address}</div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm">
                          <div>{order.contactInfo.email}</div>
                          <div className="text-gray-500">{order.contactInfo.phone}</div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1">
                          <div>
                            <span className="font-semibold text-gray-900">{order.totalPhotos}</span>
                            <span className="text-gray-500 text-sm ml-1">fotografija</span>
                          </div>
                          {order.products && order.products.length > 0 && (
                            <div>
                              <span className="font-semibold text-orange-600">{order.products.length}</span>
                              <span className="text-gray-500 text-sm ml-1">proizvoda</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          order.status === 'pending' || order.status === 'Na Čekanju' ? 'bg-yellow-100 text-yellow-700' :
                          order.status === 'completed' || order.status === 'Završeno' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {order.status === 'pending' || order.status === 'Na Čekanju' ? 'Na Čekanju' : 
                           order.status === 'completed' || order.status === 'Završeno' ? 'Završeno' : 
                           order.status === 'U Obradi' ? 'U Obradi' : 'Obrađuje se'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => handleDownload(order.orderNumber, order.zipFilePath)}
                            className="bg-orange-600 hover:bg-orange-700 text-white gap-1"
                          >
                            <Download size={16} />
                            Preuzmi
                          </Button>
                          {(order.status === 'pending' || order.status === 'Na Čekanju' || order.status === 'U Obradi') && (
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(order.orderNumber, 'completed')}
                              className="gap-1 border-2 bg-green-50 hover:bg-green-100"
                            >
                              <CheckCircle size={16} />
                              Završi
                            </Button>
                          )}
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(order.orderNumber)}
                            className="gap-1 bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 size={16} />
                            Obriši
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;